import { Icons } from '@eppendorf/vnls-css/dist';
import { Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { useQueries } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { DeviceNameCell } from '$components/device/name-cell';
import {
  AssociatedDevicesList,
  AssociatedDevicesCard,
} from '$features/devices/associated-devices/associated-devices-list/associated-devices-list';
import styles from '$features/devices/associated-devices/associated-devices-list/associated-devices-list.module.scss';
import { createDeviceQuery, useGetDevice } from '$features/devices/devices.api';
import { AssociatedDevicesModel } from '$features/devices/devices.types';

export function AssociatedDevices() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [columnsData, setColumnsData] = useState<AssociatedDevicesModel[]>([]);

  const { data, isLoading: isBoxDeviceLoading } = useGetDevice({
    manufacturer: searchParams.get('manufacturer') ?? '',
    serialNumber: searchParams.get('serialNumber') ?? '',
  });

  const queryResults = useQueries({
    queries:
      data?.connectedDevices?.map((serialNumber: string) =>
        createDeviceQuery({
          manufacturer: 'Eppendorf',
          serialNumber,
          enabled: !isBoxDeviceLoading,
        }),
      ) || [],
  });

  const allQueriesCompleted =
    !isBoxDeviceLoading &&
    queryResults.length === (data?.connectedDevices?.length ?? 0) &&
    queryResults.every((result) => result.isSuccess || result.isError);

  /* eslint-disable security/detect-object-injection -- Improve in future */
  useEffect(() => {
    if (allQueriesCompleted) {
      const mappedData: AssociatedDevicesModel[] = [];

      for (let i = 0; i < queryResults.length; i += 1) {
        const device: AssociatedDevicesModel = {
          ...queryResults[i].data,
          serialNumber:
            queryResults[i].data?.serialNumber ?? data?.connectedDevices?.[i] ?? '',
          manufacturer: queryResults[i].data?.manufacturer ?? 'Eppendorf',
          name: queryResults[i].data?.name ?? '',
          // TODO: is not implemented yet
          connectionType: 'wired',
        };
        mappedData.push(device);
      }

      setColumnsData(mappedData.sort((a, b) => a.name.localeCompare(b.name)));
    }
  }, [allQueriesCompleted]);
  /* eslint-enable security/detect-object-injection */

  return (
    <AssociatedDevicesList
      loading={isBoxDeviceLoading || !allQueriesCompleted}
      showEmptyState={
        !isBoxDeviceLoading && allQueriesCompleted && columnsData.length === 0
      }
      icon="not-paired"
      emptyTitle={t('deviceDetail.boxConnectedDevicesTable.emptyTitle')}
      emptyMessage={t('deviceDetail.boxConnectedDevicesTable.emptyMessage')}
    >
      {columnsData.map((device) => (
        <AssociatedDevicesCard
          key={device.serialNumber}
          className={styles.associatedDeviceCardWithSingleRow}
        >
          <AssociatedDevicesCard.Status>
            <Icon
              className={styles.associatedDeviceCardIcon}
              size={IconSizeClasses.Responsive}
              name={device.connectionType as Icons}
            />
          </AssociatedDevicesCard.Status>
          <AssociatedDevicesCard.Content>
            <DeviceNameCell device={device} />
          </AssociatedDevicesCard.Content>
        </AssociatedDevicesCard>
      ))}
    </AssociatedDevicesList>
  );
}
